import { Directive, Inject, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AuthService, UIOrg } from '../../../auth/service/auth.service';
import { NotificationService } from '../../notification-service/notification.service';
import { OrgPickerDialogComponent } from '../org-picker-dialog/org-picker-dialog.component';
import { StateStorageItem } from '../../../core/services/state-storage-base.service';

export interface StorageServiceInterface<T> {
  update(id: string, item: T): Observable<T>;
  create(item: T, orgId?: string): Observable<T>;
}

export interface NamedStorageItem extends StateStorageItem {
  name: string;
}

@Directive()
export abstract class BaseItemRendererComponent<T extends NamedStorageItem> {
  protected abstract storageService: StorageServiceInterface<T>;

  constructor(
    @Inject('item') public item: T,
    @Inject('allowDelete') public allowDelete: boolean,
    @Inject('showCreator') public showCreator: boolean,
    @Inject('onItemSelected') private onItemSelected: (item: T) => void,
    @Inject('onItemDeleted') private onItemDeleted: (item: T) => void,
    protected dialog: MatDialog,
    protected notificationService: NotificationService,
    protected authService: AuthService
  ) {}

  selectItem() {
    if (this.item) {
      this.onItemSelected(this.item);
    }
  }

  deleteItem(event: Event) {
    event.stopPropagation();
    if (this.item) {
      this.onItemDeleted(this.item);
    }
  }

  onToggleScope(event: Event) {
    event.stopPropagation();

    if (!this.item || !this.storageService) return;

    const newScope = this.isPublic() ? 'org' : 'public';
    const updatedItem: T = {
      ...this.item,
      scope: newScope,
    };

    this.storageService.update(this.item.id, updatedItem).subscribe({
      next: (updated) => {
        // Update the local item to reflect the change
        Object.assign(this.item, updated);
        this.notificationService.success(
          `${this.getItemTypeName()} ${this.isPublic() ? 'made public' : 'made private'}`
        );
      },
      error: () => {
        this.notificationService.error(`Failed to update ${this.getItemTypeName()} scope`);
      },
    });
  }

  onCopyToOrg(event: Event) {
    event.stopPropagation();

    if (!this.item || !this.storageService) return;

    // Open org picker dialog
    const dialogRef = this.dialog.open(OrgPickerDialogComponent, {
      width: '500px',
      data: {
        title: `Copy ${this.getItemTypeName()} to Organization`,
        message: `Select the organization where you want to copy "${this.item.name}"`,
        currentOrgId: this.authService.getSelectedOrgIdValue,
      },
    });

    dialogRef.afterClosed().subscribe((selectedOrg: UIOrg) => {
      if (selectedOrg) {
        this.copyToOrganization(selectedOrg);
      }
    });
  }

  private copyToOrganization(targetOrg: UIOrg) {
    if (!this.item || !this.storageService) return;

    const currentOrgId = this.authService.getSelectedOrgIdValue;
    const isSameOrg = targetOrg.id === currentOrgId;

    // Create a copy of the item
    const copiedItem: T = {
      ...this.item,
      id: '', // Will be assigned by the service
      name: isSameOrg ? `${this.item.name} (Copy)` : this.item.name,
      createdAt: undefined,
      updatedAt: undefined,
      createdBy: undefined,
      scope: 'org', // Reset to org scope for the copy
    };

    // Create the item in the target org
    this.storageService.create(copiedItem, targetOrg.id).subscribe({
      next: () => {
        this.notificationService.success(
          `${this.getItemTypeName()} "${this.item.name}" copied to ${targetOrg.name}`
        );
      },
      error: () => {
        this.notificationService.error(`Failed to copy ${this.getItemTypeName()} to ${targetOrg.name}`);
      },
    });
  }

  isPublic(): boolean {
    return this.item.scope === 'public';
  }

  getScopeToggleText(): string {
    return this.isPublic() ? 'Make Private' : 'Make Public';
  }

  getScopeToggleIcon(): string {
    return this.isPublic() ? 'lock' : 'public';
  }

  // Abstract method that subclasses should implement to provide item type name for notifications
  protected abstract getItemTypeName(): string;
}